<script setup lang="ts">
// COMPONENTS
import { GSkeleton, GSkeletonItem } from '@flash-global66/g-skeleton';
import LayoutContentSkeleton from '@/shared/layouts/components/LayoutContentSkeleton/index.vue';
import SidebarMenuSkeleton from '@/shared/layouts/components/SidebarMenuSkeleton/index.vue';

// TYPES
import type { AppShellSkeletonProps } from '@/shared/layouts/types/mainLayout.type';

/**
 * Placeholder de MainLayout completo para la primera navegación, mientras los guards
 * corren y la app real todavía no se montó. Copia la geometría de Navbar, Sidebar y la
 * columna de contenido para que el layout real lo reemplace sin saltos.
 */
defineOptions({ name: 'AppShellSkeleton' });

withDefaults(defineProps<AppShellSkeletonProps>(), {
  variant: 'generic',
  hideNavbar: false,
  hideSidebar: false,
});
</script>

<template>
  <div
    role="status"
    aria-busy="true"
    aria-label="Cargando"
    data-test="app:shell-skeleton"
    class="h-screen flex flex-col overflow-hidden"
  >
    <div
      v-if="!hideNavbar"
      data-test="app:shell-skeleton-navbar"
      class="shrink-0 flex items-center bg-white px-6 box-content border-b-4 border-blue-1 h-[var(--height-header-navbar)]"
    >
    <GSkeleton animated class="flex justify-between items-center">
      <template #template>
        <div class="flex items-center">
          <div class="hidden lg:block mr-8">
            <GSkeletonItem variant="rect" class="h-7 w-7 rounded" />
          </div>
          <GSkeletonItem variant="rect" class="h-[38px] w-40 rounded-lg" />
        </div>

        <div class="flex gap-x-5 items-center">
          <GSkeletonItem variant="rect" class="h-6 w-6 rounded" />
          <span class="w-px bg-inverse-bg h-10" />
          <div class="hidden lg:flex items-center">
            <GSkeletonItem variant="circle" class="h-8 w-8" />
            <div class="flex flex-col gap-1 ml-3">
              <GSkeletonItem variant="text" class="h-4 w-28" />
              <GSkeletonItem variant="text" class="h-3 w-24" />
            </div>
            <GSkeletonItem variant="rect" class="h-[18px] w-[18px] ml-4 rounded" />
          </div>
          <div class="lg:hidden">
            <GSkeletonItem variant="rect" class="h-7 w-7 rounded" />
          </div>
        </div>
      </template>
    </GSkeleton>
    </div>

    <div class="flex-1 min-h-0 flex">
      <div
        v-if="!hideSidebar"
        data-test="app:shell-skeleton-sidebar"
        class="hidden lg:flex flex-col w-full max-w-[300px] shrink-0 bg-white py-8"
      >
        <div class="flex-1 overflow-hidden px-5">
          <SidebarMenuSkeleton />
        </div>
        <GSkeleton animated class="px-4 pt-4">
          <template #template>
            <GSkeletonItem variant="rect" class="h-12 w-full rounded-lg" />
          </template>
        </GSkeleton>
      </div>

      <div class="flex-1 flex flex-col min-w-0 h-full relative">
        <img
          src="/img/dashboard/cover.webp"
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchpriority="high"
          class="absolute inset-0 w-full h-full object-cover pointer-events-none -z-10"
        />
        <LayoutContentSkeleton :variant="variant" :is-live-region="false" />
        <footer class="pt-2 pb-3 px-4 md:px-8 mt-auto">
          <div class="border-t border-gray-8 pt-2 min-h-sm" />
        </footer>
      </div>
    </div>
  </div>
</template>
